import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './styles/globals.css';
import { installStaleChunkReload } from './utils/staleChunk';

// Old tab + new deploy -> the lazy route's hashed chunk is gone; reload once.
installStaleChunkReload();

// Baked at build time by vite.config.ts (git short-SHA per deploy).
declare const __SENTRY_RELEASE__: string;

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  // Monitoring is optional and substantial (especially Replay). Keep it out
  // of the critical portal bundle while preserving the same production
  // configuration whenever a DSN is present.
  void import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: SENTRY_DSN,
      // Overridable so a build can be tagged with an explicit Sentry
      // environment (e.g. the isolated devbox dev stack tags "development")
      // independent of Vite's own MODE, which stays "production" for a
      // production-style build even when the DSN points at a different
      // Sentry project.
      environment:
        import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'production',
      // Release Health: a per-deploy release is REQUIRED for Crash-Free
      // Sessions to populate (without it Sentry shows "Start setup" even
      // though events arrive). @sentry/react v8 auto-tracks browser sessions
      // by default, so release + reliable delivery is all that's needed.
      release:
        typeof __SENTRY_RELEASE__ !== 'undefined' ? __SENTRY_RELEASE__ : undefined,
      // Same-origin relay through the portal's /api proxy -> backend
      // (/api/v1/public/sentry-tunnel), so ad-blockers / privacy lists that
      // block *.ingest.sentry.io don't silently drop session pings — the #1
      // reason Release Health stays on "Start setup".
      tunnel: '/api/v1/public/sentry-tunnel',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
    });
  }).catch(() => {
    // Monitoring must never prevent the vendor portal from booting.
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
